import type { CSSProperties } from "react";
import {
  normalizeCommentPlatform,
  type NativeCommentViewModel,
} from "@/components/social-proof/nativeCommentModel";
import { displayAuthorName } from "@/lib/commentLabels";

function nameOf(model: NativeCommentViewModel): string {
  if (model.displayHandle) return model.displayHandle;
  return displayAuthorName(model.author, model.sourceKind || model.platform);
}

function platformOf(model: NativeCommentViewModel): string {
  return normalizeCommentPlatform(model.platform, model.sourceKind);
}

function initials(name: string): string {
  const cleaned = name.replace(/^Czytelniczka\s+/i, "").trim() || name;
  const parts = cleaned.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

function avatarBg(platform: string): string {
  if (platform === "instagram") return "#e1306c";
  if (platform === "tiktok") return "#111111";
  if (platform === "youtube") return "#ff0000";
  if (platform === "messenger") return "#7b61ff";
  return "#1877f2";
}

function Avatar({
  name,
  platform,
  size,
  avatarUrl,
}: {
  name: string;
  platform: string;
  size: number;
  avatarUrl?: string | null;
}) {
  const style: CSSProperties = {
    width: size,
    height: size,
    borderRadius: "999px",
    background: avatarBg(platform),
    color: "#ffffff",
    fontSize: size <= 32 ? 10 : 12,
    fontWeight: 700,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    overflow: "hidden",
    fontFamily: "system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
  };
  if (avatarUrl) {
    return (
      <div style={style}>
        <img
          src={avatarUrl}
          alt=""
          style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
          crossOrigin="anonymous"
        />
      </div>
    );
  }
  return <div style={style}>{initials(name)}</div>;
}

function IgSkin({ model }: { model: NativeCommentViewModel }) {
  const name = nameOf(model).replace(/^@/, "");
  const likes = model.likeCount || 0;
  return (
    <div
      style={{
        background: "#ffffff",
        borderRadius: 16,
        padding: "12px 12px",
        fontFamily: "system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
      }}
    >
      <div style={{ display: "flex", gap: 12 }}>
        <Avatar name={model.author || name} platform="instagram" size={40} avatarUrl={model.avatarUrl} />
        <div style={{ minWidth: 0, flex: 1 }}>
          <p style={{ margin: 0, fontSize: 13, lineHeight: 1.35, color: "#0f172a" }}>
            <span style={{ fontWeight: 700 }}>{name}</span>{" "}
            <span style={{ whiteSpace: "pre-wrap", fontWeight: 400 }}>{model.text}</span>
          </p>
          {likes > 0 ? (
            <div style={{ marginTop: 6, fontSize: 11, color: "#94a3b8" }}>{likes} polubień</div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function FbSkin({ model }: { model: NativeCommentViewModel }) {
  const name = nameOf(model);
  return (
    <div
      style={{
        background: "#f0f2f5",
        borderRadius: 16,
        padding: 12,
        fontFamily: "system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
      }}
    >
      <div style={{ display: "flex", gap: 10 }}>
        <Avatar name={model.author || name} platform="facebook" size={32} avatarUrl={model.avatarUrl} />
        <div style={{ minWidth: 0, flex: 1 }}>
          <div
            style={{
              background: "#ffffff",
              borderRadius: 16,
              padding: "8px 12px",
              boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
            }}
          >
            <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>{name}</div>
            <p
              style={{
                margin: "2px 0 0",
                fontSize: 13,
                lineHeight: 1.35,
                color: "#1e293b",
                whiteSpace: "pre-wrap",
              }}
            >
              {model.text}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function TikTokSkin({ model }: { model: NativeCommentViewModel }) {
  const name = nameOf(model).replace(/^@/, "");
  const likes = model.likeCount || 0;
  return (
    <div
      style={{
        background: "#ffffff",
        borderRadius: 16,
        padding: 12,
        fontFamily: "system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
      }}
    >
      <div style={{ display: "flex", gap: 12 }}>
        <Avatar name={model.author || name} platform="tiktok" size={40} avatarUrl={model.avatarUrl} />
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#64748b" }}>{name}</div>
          <p
            style={{
              margin: "2px 0 0",
              fontSize: 14,
              lineHeight: 1.35,
              color: "#0f172a",
              whiteSpace: "pre-wrap",
            }}
          >
            {model.text}
          </p>
          {likes > 0 ? (
            <div style={{ marginTop: 6, fontSize: 11, color: "#94a3b8" }}>{likes}</div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function YouTubeSkin({ model }: { model: NativeCommentViewModel }) {
  const name = nameOf(model);
  const likes = model.likeCount || 0;
  return (
    <div
      style={{
        background: "#ffffff",
        borderRadius: 16,
        padding: 12,
        fontFamily: "system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
      }}
    >
      <div style={{ display: "flex", gap: 12 }}>
        <Avatar name={model.author || name} platform="youtube" size={40} avatarUrl={model.avatarUrl} />
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: "#0f172a" }}>{name}</div>
          <p
            style={{
              margin: "4px 0 0",
              fontSize: 13,
              lineHeight: 1.35,
              color: "#1e293b",
              whiteSpace: "pre-wrap",
            }}
          >
            {model.text}
          </p>
          {likes > 0 ? (
            <div
              style={{
                marginTop: 8,
                fontSize: 11,
                fontWeight: 600,
                color: "#64748b",
              }}
            >
              👍 {likes}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function DmSkin({ model }: { model: NativeCommentViewModel }) {
  const name = nameOf(model);
  const isIg = model.sourceKind === "dm_ig" || platformOf(model) === "instagram";
  return (
    <div
      style={{
        background: isIg ? "#0f172a" : "#eef2ff",
        borderRadius: 16,
        padding: 12,
        fontFamily: "system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
      }}
    >
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8 }}>
        <Avatar
          name={model.author || name}
          platform={isIg ? "instagram" : "messenger"}
          size={32}
          avatarUrl={model.avatarUrl}
        />
        <div style={{ fontSize: 12, fontWeight: 700, color: isIg ? "#ffffff" : "#1e293b" }}>
          {name}
        </div>
      </div>
      <div
        style={{
          maxWidth: "92%",
          borderRadius: 16,
          borderTopLeftRadius: 6,
          padding: "8px 12px",
          fontSize: 13,
          lineHeight: 1.35,
          whiteSpace: "pre-wrap",
          background: isIg ? "#334155" : "#ffffff",
          color: isIg ? "#ffffff" : "#0f172a",
        }}
      >
        {model.text}
      </div>
    </div>
  );
}

/** Inline-hex skins only — avoids Tailwind oklch hangs in html-to-image. */
export default function CommentPngSkin({ model }: { model: NativeCommentViewModel }) {
  const sk = String(model.sourceKind || "").toLowerCase();
  if (sk === "dm_fb" || sk === "dm_ig") return <DmSkin model={model} />;
  const platform = platformOf(model);
  if (platform === "tiktok") return <TikTokSkin model={model} />;
  if (platform === "youtube") return <YouTubeSkin model={model} />;
  if (platform === "facebook") return <FbSkin model={model} />;
  return <IgSkin model={model} />;
}
